import React, { useState, useRef, useEffect } from 'react';
import {
  Search, User, CreditCard, ClipboardList, Award, FileText,
  Edit2, Save, X, ChevronDown, Download, Camera,
  MapPin, Briefcase, Shield
} from 'lucide-react';
import { db } from '../../firebase/config';
import { collection, query, where, getDocs, updateDoc, doc } from 'firebase/firestore';
import { useRealtime } from '../../contexts/RealtimeContext';
import { uploadFile } from '../../firebase/r2storage';
import { refreshUrl, isUrlExpired } from '../../utils/urlRefreshHelper';
import toast from 'react-hot-toast';
import jsPDF from 'jspdf';

// ─── helpers ────────────────────────────────────────────────────────────────
const fmt = v => v || '—';
const fmtDate = v => {
  if (!v) return '—';
  try { return new Date(v?.seconds ? v.seconds * 1000 : v).toLocaleDateString('en-IN'); }
  catch { return '—'; }
};
const CACHE_TTL = 5 * 60 * 1000; // 5 min

// ─── Certificate types ────────────────────────────────────────────────────────
const CERTIFICATE_TYPES = [
  { id: 'transfer',   label: 'Transfer Certificate' },
  { id: 'character',  label: 'Character Certificate' },
  { id: 'gatepass',   label: 'Gate Pass' },
  { id: 'fees',       label: 'Fees Certificate' },
  { id: 'studying',   label: 'Studying Certificate' },
  { id: 'promotion',  label: 'Promotion Form' },
];

// ─── Certificate PDF ──────────────────────────────────────────────────────────
function buildCertPDF(type, student, institute, setLoadingCert) {
  if (setLoadingCert) setLoadingCert(type.id);
  // Small timeout to let spinner render
  setTimeout(() => {
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const W = 210;

  // Outer border
  pdf.setDrawColor(30, 80, 160);
  pdf.setLineWidth(2);
  pdf.rect(10, 10, W - 20, 277);
  pdf.setLineWidth(0.5);
  pdf.rect(13, 13, W - 26, 271);

  // ── HEADER ─────────────────────────────────────────────────────────────────
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(18);
  pdf.setTextColor(30, 80, 160);
  pdf.text(institute.name || 'Institution Name', W / 2, 28, { align: 'center' });

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  pdf.setTextColor(60, 60, 60);
  const headerLines = [
    institute.address || '',
    [institute.email, institute.phone].filter(Boolean).join('  |  '),
    institute.affiliationNumber ? `Affiliation No.: ${institute.affiliationNumber}` : ''
  ].filter(Boolean);
  let hy = 35;
  headerLines.forEach(line => {
    if (line) { pdf.text(line, W / 2, hy, { align: 'center' }); hy += 5; }
  });

  // Divider
  pdf.setDrawColor(30, 80, 160);
  pdf.setLineWidth(1);
  pdf.line(20, hy + 1, W - 20, hy + 1);
  hy += 8;

  // Certificate title
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(14);
  pdf.setTextColor(0, 0, 0);
  pdf.text(type.label.toUpperCase(), W / 2, hy, { align: 'center' });
  hy += 10;

  // Date right-aligned
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);
  pdf.text(`Date: ${new Date().toLocaleDateString('en-IN')}`, W - 20, hy, { align: 'right' });
  hy += 10;

  // ── BODY ───────────────────────────────────────────────────────────────────
  const name = student.name || '';
  const father = student.fatherName || '';
  const mother = student.motherName || '';
  const phone = student.phone || '';
  const reg = student.registrationNumber || '';

  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'normal');
  const lm = 20; // left margin
  const rm = 20; // right margin
  const textWidth = W - lm - rm;

  let bodyText = '';

  if (type.id === 'transfer') {
    bodyText = `This is to certify that ${name}, son/daughter of Sh. ${father} and Smt. ${mother}, bearing Registration No. ${reg}, was a bonafide student of this institution.\n\nThis Transfer Certificate is issued on request.`;
  } else if (type.id === 'character') {
    bodyText = `This is to certify that ${name}, son/daughter of Sh. ${father} and Smt. ${mother}, Reg. No. ${reg}, was a student of this institution and maintained good moral character throughout the period of study. No disciplinary action was taken against the student during the tenure.`;
  } else if (type.id === 'gatepass') {
    bodyText = `GATE PASS\n\nThis authorizes ${name} (Reg. No. ${reg}) to exit the institution premises.\n\nParent/Guardian: ${father}\nContact: ${phone}`;
  } else if (type.id === 'fees') {
    bodyText = `This is to certify that ${name}, son/daughter of Sh. ${father} and Smt. ${mother}, Reg. No. ${reg}, has paid the required fees for the current academic session. No dues are pending against the student as on date.`;
  } else if (type.id === 'studying') {
    bodyText = `This is to certify that ${name}, son/daughter of Sh. ${father} and Smt. ${mother}, Reg. No. ${reg}, is currently enrolled as a bonafide student of this institution for the current academic session.`;
  } else if (type.id === 'promotion') {
    bodyText = `PROMOTION FORM\n\nStudent Name: ${name}\nFather's Name: ${father}\nMother's Name: ${mother}\nMobile: ${phone}\nReg. No.: ${reg}\n\nThe above-mentioned student has been promoted to the next batch/class as per academic records and institutional guidelines.`;
  }

  // Render paragraphs
  const paragraphs = bodyText.split('\n');
  paragraphs.forEach(para => {
    if (para.trim() === '') {
      hy += 6;
    } else {
      const wrapped = pdf.splitTextToSize(para, textWidth);
      wrapped.forEach(line => {
        pdf.text(line, lm, hy);
        hy += 7;
      });
    }
  });

  // Signature
  const sigY = Math.max(hy + 30, 255);
  pdf.line(W - 80, sigY, W - rm, sigY);
  pdf.setFontSize(10);
  pdf.text('Principal / Authorized Signatory', W - rm, sigY + 5, { align: 'right' });

  pdf.save(`${type.label.replace(/ /g, '_')}_${name}.pdf`);
  if (setLoadingCert) setLoadingCert(null);
  }, 100);
}

// ─── Transfer Certificate Form ────────────────────────────────────────────────
const TC_FIELDS = [
  { name: 'tcName',           label: '1. Name of Pupil' },
  { name: 'tcFather',         label: '2. Name of Father' },
  { name: 'tcMother',         label: '3. Name of Mother' },
  { name: 'tcNationality',    label: '4. Nationality' },
  { name: 'tcCategory',       label: '5. Category' },
  { name: 'tcDOBFigures',     label: '6. Date of Birth (figures)' },
  { name: 'tcDOBWords',       label: '   Date of Birth (words)' },
  { name: 'tcClassFigures',   label: '7. Last Class (figures)' },
  { name: 'tcClassWords',     label: '   Last Class (words)' },
  { name: 'tcPromotedTo',     label: '   Qualified for promotion to' },
  { name: 'tcLastSchool',     label: '8. School last studied' },
  { name: 'tcAdmissionDate',  label: '9. Date of admission / Class' },
  { name: 'tcBoardResult',    label: '10. Board Exam last taken & result' },
  { name: 'tcSubjects',       label: '11. Subjects' },
  { name: 'tcWorkingDays',    label: '12. Total working days' },
  { name: 'tcPresentDays',    label: '13. Days present' },
  { name: 'tcConduct',        label: '14. General Conduct' },
  { name: 'tcReason',         label: '15. Reason for leaving' },
  { name: 'tcGames',          label: '16. Games / Extra-curricular' },
  { name: 'tcAppDate',        label: '17. Date of application' },
  { name: 'tcRemarks',        label: '18. Any other remark' },
];

function TCForm({ student, institute }) {
  const initVals = {
    tcName: student.name || '',
    tcFather: student.fatherName || '',
    tcMother: student.motherName || '',
    tcNationality: 'INDIAN', tcCategory: '',
    tcDOBFigures: '', tcDOBWords: '', tcClassFigures: '', tcClassWords: '',
    tcPromotedTo: '', tcLastSchool: institute.name || '',
    tcAdmissionDate: '', tcBoardResult: 'PASS', tcSubjects: '',
    tcWorkingDays: '', tcPresentDays: '', tcConduct: 'GOOD',
    tcReason: '', tcGames: '', tcAppDate: new Date().toLocaleDateString('en-IN'),
    tcRemarks: '',
  };
  const [vals, setVals] = useState(initVals);

  const handleChange = e => setVals(p => ({ ...p, [e.target.name]: e.target.value }));

  const downloadTC = () => {
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const W = 210;

    pdf.setDrawColor(30, 80, 160); pdf.setLineWidth(2);
    pdf.rect(10, 10, W - 20, 277);
    pdf.setLineWidth(0.5); pdf.rect(13, 13, W - 26, 271);

    // Header
    pdf.setFont('helvetica', 'bold'); pdf.setFontSize(16);
    pdf.setTextColor(30, 80, 160);
    pdf.text(institute.name || 'Institution', W / 2, 27, { align: 'center' });

    pdf.setFont('helvetica', 'normal'); pdf.setFontSize(9); pdf.setTextColor(60, 60, 60);
    let hy = 33;
    if (institute.address) { pdf.text(institute.address, W / 2, hy, { align: 'center' }); hy += 5; }
    if (institute.phone || institute.email) {
      pdf.text([institute.phone, institute.email].filter(Boolean).join('  |  '), W / 2, hy, { align: 'center' });
      hy += 5;
    }
    if (institute.affiliationNumber) {
      pdf.text(`Affiliation No.: ${institute.affiliationNumber}`, W / 2, hy, { align: 'center' });
      hy += 5;
    }

    pdf.setDrawColor(30, 80, 160); pdf.setLineWidth(1);
    pdf.line(20, hy + 1, W - 20, hy + 1); hy += 8;

    pdf.setFont('helvetica', 'bold'); pdf.setFontSize(13); pdf.setTextColor(0, 0, 0);
    pdf.text('TRANSFER CERTIFICATE', W / 2, hy, { align: 'center' }); hy += 10;

    pdf.setFont('helvetica', 'normal'); pdf.setFontSize(10);
    pdf.text(`Date: ${new Date().toLocaleDateString('en-IN')}`, W - 20, hy, { align: 'right' }); hy += 6;

    TC_FIELDS.forEach(f => {
      const label = f.label;
      const value = vals[f.name] || '';
      pdf.setFont('helvetica', 'bold'); pdf.setFontSize(10);
      pdf.text(`${label}:`, 20, hy);
      pdf.setFont('helvetica', 'normal');
      pdf.text(value, 105, hy);
      hy += 8;
      if (hy > 260) { pdf.addPage(); hy = 20; }
    });

    pdf.line(W - 80, 265, W - 20, 265);
    pdf.setFontSize(10);
    pdf.text('Principal / Authorized Signatory', W - 20, 270, { align: 'right' });
    pdf.save(`Transfer_Certificate_${vals.tcName || 'student'}.pdf`);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Left: form */}
      <div className="space-y-3">
        <h4 className="font-semibold text-gray-700 dark:text-gray-200 text-sm">Fill Transfer Certificate Fields</h4>
        {TC_FIELDS.map(f => (
          <div key={f.name}>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">{f.label}</label>
            <input
              name={f.name}
              value={vals[f.name]}
              onChange={handleChange}
              className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
        ))}
        <button
          onClick={downloadTC}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium mt-4"
        >
          <Download className="w-4 h-4" /> Download Transfer Certificate
        </button>
      </div>

      {/* Right: live preview */}
      <div className="sticky top-20 self-start">
        <h4 className="font-semibold text-gray-700 dark:text-gray-200 text-sm mb-3">Live Preview</h4>
        <div className="border-2 border-blue-200 dark:border-blue-700 rounded-xl p-5 bg-white dark:bg-gray-900 text-xs space-y-1 max-h-[80vh] overflow-y-auto font-mono">
          <p className="text-center font-bold text-blue-700 text-sm">{institute.name || 'Institution Name'}</p>
          {institute.address && <p className="text-center text-gray-500">{institute.address}</p>}
          {(institute.phone || institute.email) && (
            <p className="text-center text-gray-500">{[institute.phone, institute.email].filter(Boolean).join(' | ')}</p>
          )}
          {institute.affiliationNumber && (
            <p className="text-center text-gray-500">Affiliation No.: {institute.affiliationNumber}</p>
          )}
          <hr className="border-blue-300 my-2" />
          <p className="text-center font-bold text-sm tracking-wide">TRANSFER CERTIFICATE</p>
          <p className="text-right text-gray-500">Date: {new Date().toLocaleDateString('en-IN')}</p>
          <div className="mt-2 space-y-1">
            {TC_FIELDS.map(f => (
              <div key={f.name} className="flex gap-1">
                <span className="text-gray-500 min-w-[140px] shrink-0">{f.label}:</span>
                <span className="font-semibold text-gray-800 dark:text-gray-100 break-all">
                  {vals[f.name] || <span className="text-gray-300 italic">not filled</span>}
                </span>
              </div>
            ))}
          </div>
          <p className="text-right text-gray-500 pt-4 border-t border-gray-200 mt-4">Principal / Authorized Signatory</p>
        </div>
      </div>
    </div>
  );
}

// ─── Section wrapper ──────────────────────────────────────────────────────────
function Section({ title, icon: Icon, children }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
      <div className="flex items-center gap-2 px-6 py-4 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-750">
        {Icon && <Icon className="w-5 h-5 text-blue-600" />}
        <h3 className="font-semibold text-gray-800 dark:text-white text-base">{title}</h3>
      </div>
      <div className="p-6">{children}</div>
    </div>
  );
}

// ─── Field ────────────────────────────────────────────────────────────────────
function Field({ label, value, editing, name, onChange, type = 'text', options }) {
  if (editing) {
    if (options) return (
      <div>
        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">{label}</label>
        <select name={name} value={value || ''} onChange={onChange}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
          <option value="">Select…</option>
          {options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>
    );
    return (
      <div>
        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">{label}</label>
        <input type={type} name={name} value={value || ''} onChange={onChange}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
      </div>
    );
  }
  return (
    <div>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">{label}</p>
      <p className="text-sm font-medium text-gray-800 dark:text-white">{fmt(value)}</p>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function StudentInfo({ coachingId }) {
  const { userProfile, currentUser } = useRealtime();
  const resolvedId = coachingId || userProfile?.coachingId;

  // Institute details fetched from coaching profile doc
  const [institute, setInstitute] = useState({
    name: '', address: '', phone: '', email: '', affiliationNumber: '',
  });

  // Fetch coaching admin profile to get institute details
  // Same source as ReportCards: userProfile.reportCardInstituteDefaults + userProfile.coachingName
  useEffect(() => {
    if (!userProfile) return;
    const d = userProfile.reportCardInstituteDefaults || {};
    setInstitute({
      name:              userProfile.coachingName || userProfile.instituteName || userProfile.institutionName || '',
      address:           d.address || '',
      phone:             d.institutePhone || userProfile.phone || '',
      email:             d.instituteEmail || userProfile.email || '',
      affiliationNumber: d.affiliationNumber || '',
    });
  }, [userProfile]); // eslint-disable-line

  // Search
  const [searchBy,     setSearchBy]     = useState('name');
  const [searchInput,  setSearchInput]  = useState('');
  const [searching,    setSearching]    = useState(false);
  const [results,      setResults]      = useState([]);
  const [showDrop,     setShowDrop]     = useState(false);

  // Selected student
  const [student,      setStudent]      = useState(null);
  const [activeTab,    setActiveTab]    = useState('profile');
  const [editing,      setEditing]      = useState(false);
  const [formData,     setFormData]     = useState({});
  const [saving,       setSaving]       = useState(false);

  // Linked data
  const [fees,         setFees]         = useState([]);
  const [feesBatchNames, setFeesBatchNames] = useState({}); // batchId -> batchName for fees
  const [attendance,   setAttendance]   = useState([]);
  const [batchNames,   setBatchNames]   = useState({}); // batchId -> batchName for attendance
  const [testResults,  setTestResults]  = useState([]);
  const [loadingData,  setLoadingData]  = useState(false);

  // Photo
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoRef = useRef();

  // TC form visibility
  const [showTC, setShowTC] = useState(false);
  const [loadingCert, setLoadingCert] = useState(null);
  const cacheGet = key => {
    try {
      const ts = localStorage.getItem(`${key}_ts`);
      if (!ts || Date.now() - parseInt(ts) > CACHE_TTL) return null;
      return JSON.parse(localStorage.getItem(key));
    } catch { return null; }
  };
  const cacheSet = (key, data) => {
    try {
      localStorage.setItem(key, JSON.stringify(data));
      localStorage.setItem(`${key}_ts`, Date.now().toString());
    } catch {}
  };

  // ── Search ────────────────────────────────────────────────────────────────
  const handleSearch = async () => {
    if (!searchInput.trim()) return toast.error('Enter a search value');
    setSearching(true);
    setShowDrop(false);

    const cacheKey = `si_search_${resolvedId}_${searchBy}_${searchInput.trim().toLowerCase()}`;
    const cached = cacheGet(cacheKey);
    if (cached) { setResults(cached); setShowDrop(true); setSearching(false); return; }

    try {
      let data = [];
      if (searchBy === 'email') {
        const snap = await getDocs(query(
          collection(db, 'students'),
          where('email', '==', searchInput.toLowerCase().trim()),
          where('coachingId', '==', resolvedId)
        ));
        data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      } else {
        const snap = await getDocs(query(
          collection(db, 'students'),
          where('coachingId', '==', resolvedId)
        ));
        const term = searchInput.toLowerCase().trim();
        data = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(s => (s.name || s.fullName || '').toLowerCase().includes(term));
      }
      cacheSet(cacheKey, data);
      setResults(data);
      setShowDrop(true);
      if (!data.length) toast('No students found', { icon: 'ℹ️' });
    } catch (e) {
      toast.error('Search failed: ' + e.message);
    } finally {
      setSearching(false);
    }
  };

  // ── Select student ────────────────────────────────────────────────────────
  const selectStudent = async s => {
    // Refresh profile picture URL if expired
    let photoUrl = s.profilePicture || '';
    if (photoUrl && isUrlExpired(photoUrl)) {
      try {
        const fresh = await refreshUrl(photoUrl);
        if (fresh) photoUrl = fresh;
      } catch (e) {
        console.warn('[StudentInfo] Photo URL refresh failed:', e.message);
      }
    }
    const refreshedStudent = { ...s, profilePicture: photoUrl };

    setStudent(refreshedStudent);
    setFormData({ ...refreshedStudent });
    setShowDrop(false);
    setActiveTab('profile');
    setEditing(false);
    setShowTC(false);
    setBatchNames({});
    setFeesBatchNames({});
    await loadLinkedData(s);
  };

  const loadLinkedData = async s => {
    if (!s) return;
    setLoadingData(true);
    const email = (s.email || '').toLowerCase().trim();
    const studentDocId = s.id;

    console.log('[StudentInfo] Loading data for:', { email, studentDocId });

    // ── Step 1: Resolve userId (Auth UID) from users collection via email
    let resolvedUserId = null;
    try {
      if (email) {
        const userSnap = await getDocs(query(
          collection(db, 'users'),
          where('email', '==', email)
        ));
        if (!userSnap.empty) {
          resolvedUserId = userSnap.docs[0].id;
          console.log('[StudentInfo] Resolved userId:', resolvedUserId);
        } else {
          console.log('[StudentInfo] No user found in users collection for email:', email);
        }
      }
    } catch (e) {
      console.error('[StudentInfo] Error resolving userId:', e);
    }

    // Also get batchEnrollment studentIds for this student (another possible ID)
    let enrollmentUserIds = new Set();
    try {
      const enrollSnap = await getDocs(query(
        collection(db, 'batchEnrollments'),
        where('email', '==', email),
        where('coachingId', '==', resolvedId)
      ));
      enrollSnap.docs.forEach(d => {
        const uid = d.data().userId || d.data().studentId;
        if (uid) enrollmentUserIds.add(uid);
      });
      console.log('[StudentInfo] Enrollment userIds:', [...enrollmentUserIds]);
    } catch (e) {
      console.error('[StudentInfo] Error fetching enrollments:', e);
    }

    // All possible IDs to try
    const allIds = [...new Set([
      resolvedUserId,
      studentDocId,
      ...enrollmentUserIds
    ].filter(Boolean))];
    console.log('[StudentInfo] All IDs to try:', allIds);

    // ── FEES ─────────────────────────────────────────────────────────────────
    try {
      let feesData = [];

      for (const uid of allIds) {
        if (feesData.length) break;
        const snap = await getDocs(query(
          collection(db, 'studentFees'),
          where('coachingId', '==', resolvedId),
          where('studentId', '==', uid)
        ));
        feesData = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        if (feesData.length) console.log('[StudentInfo] Fees found with studentId:', uid);
      }

      // Fallback by studentEmail
      if (!feesData.length && email) {
        const snap = await getDocs(query(
          collection(db, 'studentFees'),
          where('coachingId', '==', resolvedId),
          where('studentEmail', '==', email)
        ));
        feesData = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        if (feesData.length) console.log('[StudentInfo] Fees found by studentEmail');
      }

      console.log('[StudentInfo] Total fees found:', feesData.length);
      setFees(feesData);

      // Fetch batch names for fees
      if (feesData.length) {
        const uniqueBatchIds = [...new Set(feesData.map(f => f.batchId).filter(Boolean))];
        const namesMap = {};
        await Promise.all(uniqueBatchIds.map(async bid => {
          try {
            const bSnap = await getDocs(query(collection(db, 'batches'), where('__name__', '==', bid)));
            namesMap[bid] = !bSnap.empty ? (bSnap.docs[0].data().name || bid) : bid;
          } catch { namesMap[bid] = bid; }
        }));
        setFeesBatchNames(namesMap);
      }
    } catch (e) { console.error('[StudentInfo] Fees load error:', e); setFees([]); }

    // ── ATTENDANCE ────────────────────────────────────────────────────────────
    // batchAttendance stores: { batchId, studentId (=userId), date, status }
    // We need batchIds this student is enrolled in, then query per batch+studentId
    try {
      let attData = [];

      // Get all batchIds for this student from batchEnrollments
      const batchIds = [...new Set(
        (await getDocs(query(
          collection(db, 'batchEnrollments'),
          where('coachingId', '==', resolvedId),
          where('email', '==', email)
        ))).docs.map(d => d.data().batchId).filter(Boolean)
      )];
      console.log('[StudentInfo] Student batchIds:', batchIds);

      // For each batch + each possible userId, query batchAttendance
      for (const batchId of batchIds) {
        for (const uid of allIds) {
          const snap = await getDocs(query(
            collection(db, 'batchAttendance'),
            where('batchId', '==', batchId),
            where('studentId', '==', uid)
          ));
          const records = snap.docs.map(d => ({ id: d.id, ...d.data() }));
          if (records.length) {
            console.log(`[StudentInfo] Attendance found: batchId=${batchId}, studentId=${uid}, count=${records.length}`);
            attData = [...attData, ...records];
            break; // found with this uid for this batch, move to next batch
          }
        }
      }

      // Fallback: if still nothing, try querying by studentId alone across all batches
      if (!attData.length) {
        for (const uid of allIds) {
          const snap = await getDocs(query(
            collection(db, 'batchAttendance'),
            where('studentId', '==', uid)
          ));
          if (!snap.empty) {
            attData = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            console.log(`[StudentInfo] Attendance fallback found with studentId=${uid}, count=${attData.length}`);
            break;
          }
        }
      }

      console.log('[StudentInfo] Total attendance records found:', attData.length);
      setAttendance(attData);

      // Fetch batch names for all unique batchIds in attendance
      if (attData.length) {
        const uniqueBatchIds = [...new Set(attData.map(r => r.batchId).filter(Boolean))];
        const namesMap = {};
        await Promise.all(uniqueBatchIds.map(async bid => {
          try {
            const bSnap = await getDocs(query(collection(db, 'batches'), where('__name__', '==', bid)));
            if (!bSnap.empty) namesMap[bid] = bSnap.docs[0].data().name || bid;
            else namesMap[bid] = bid; // fallback to id
          } catch { namesMap[bid] = bid; }
        }));
        setBatchNames(namesMap);
      }
    } catch (e) { console.error('[StudentInfo] Attendance load error:', e); setAttendance([]); }

    // ── RESULTS ───────────────────────────────────────────────────────────────
    try {
      let resData = [];
      if (email) {
        const snap = await getDocs(query(
          collection(db, 'studentResults'),
          where('studentEmail', '==', email)
        ));
        resData = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      }
      console.log('[StudentInfo] Total results found:', resData.length);
      setTestResults(resData);
    } catch (e) { console.error('[StudentInfo] Results load error:', e); setTestResults([]); }

    setLoadingData(false);
  };

  // ── Edit / Save ───────────────────────────────────────────────────────────
  const handleChange = e => setFormData(p => ({ ...p, [e.target.name]: e.target.value }));

  const handleSave = async () => {
    if (!student?.id) return;
    setSaving(true);
    try {
      const ref = doc(db, 'students', student.id);
      const { id, ...rest } = formData;
      await updateDoc(ref, rest);
      setStudent({ ...student, ...formData });
      setEditing(false);
      // Invalidate caches
      localStorage.removeItem(`students_${resolvedId}`);
      localStorage.removeItem(`students_${resolvedId}_ts`);
      // Also invalidate any search cache for this student
      Object.keys(localStorage).filter(k => k.startsWith('si_search_')).forEach(k => {
        localStorage.removeItem(k);
        localStorage.removeItem(`${k}_ts`);
      });
      toast.success('Student info updated!');
    } catch (e) { toast.error('Save failed: ' + e.message); }
    finally { setSaving(false); }
  };

  // ── Photo upload ──────────────────────────────────────────────────────────
  const handlePhotoUpload = async e => {
    const file = e.target.files[0];
    if (!file || !student?.id) return;
    if (!file.type.startsWith('image/')) return toast.error('Please upload an image');
    setUploadingPhoto(true);
    try {
      const reader = new FileReader();
      const b64 = await new Promise(res => { reader.onloadend = () => res(reader.result); reader.readAsDataURL(file); });
      const result = await uploadFile(file, `student-profiles/${resolvedId}/${Date.now()}_${file.name}`);
      if (result.success) {
        await updateDoc(doc(db, 'students', student.id), { profilePicture: result.url || result.fileKey });
        setStudent(p => ({ ...p, profilePicture: b64 }));
        toast.success('Photo updated!');
      } else toast.error('Upload failed: ' + result.error);
    } catch { toast.error('Photo upload failed'); }
    finally { setUploadingPhoto(false); }
  };

  // ── Attendance summary ────────────────────────────────────────────────────
  const attByBatch = attendance.reduce((acc, r) => {
    const bid = r.batchId || 'unknown';
    const bname = batchNames[bid] || r.batchName || bid;
    if (!acc[bid]) acc[bid] = { batchId: bid, batchName: bname, present: 0, total: 0 };
    acc[bid].total++;
    if (r.status === 'present' || r.status === 'late') acc[bid].present++;
    return acc;
  }, {});

  const TABS = [
    { id: 'profile',      label: 'Profile',     icon: User },
    { id: 'fees',         label: 'Fees',         icon: CreditCard },
    { id: 'attendance',   label: 'Attendance',   icon: ClipboardList },
    { id: 'results',      label: 'Results',      icon: Award },
    { id: 'certificates', label: 'Certificates', icon: FileText },
  ];

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <Search className="w-6 h-6 text-blue-600" /> Student Info
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Search and view / edit complete student profile
        </p>
      </div>

      {/* ── Search ── */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative">
            <select value={searchBy} onChange={e => setSearchBy(e.target.value)}
              className="appearance-none pl-3 pr-8 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm cursor-pointer">
              <option value="name">By Name</option>
              <option value="email">By Email</option>
            </select>
            <ChevronDown className="absolute right-2 top-3 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>
          <input type="text" value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
            placeholder={searchBy === 'name' ? 'Enter student name…' : 'Enter email…'}
            className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm" />
          <button onClick={handleSearch} disabled={searching}
            className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium flex items-center gap-2 disabled:opacity-50">
            {searching ? <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> : <Search className="w-4 h-4" />}
            Search
          </button>
        </div>

        {showDrop && results.length > 0 && (
          <div className="mt-3 border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden divide-y divide-gray-100 dark:divide-gray-700 shadow-lg">
            {results.map(r => (
              <button key={r.id} onClick={() => selectStudent(r)}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-blue-50 dark:hover:bg-blue-900/20 text-left">
                {r.profilePicture
                  ? <img src={r.profilePicture} alt="" className="w-9 h-9 rounded-full object-cover border" />
                  : <div className="w-9 h-9 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center"><User className="w-5 h-5 text-blue-600" /></div>
                }
                <div>
                  <p className="font-medium text-gray-900 dark:text-white text-sm">{r.name || r.fullName}</p>
                  <p className="text-xs text-gray-500">{r.email} · {r.registrationNumber || 'No Reg#'}</p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── Student detail ── */}
      {student && (
        <div className="space-y-4">
          {/* Banner */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl p-6 text-white">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="relative">
                {student.profilePicture
                  ? <img src={student.profilePicture} alt="" className="w-20 h-20 rounded-full object-cover border-4 border-white/30" />
                  : <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center border-4 border-white/30"><User className="w-10 h-10 text-white/80" /></div>
                }
                <button onClick={() => photoRef.current?.click()} disabled={uploadingPhoto}
                  className="absolute bottom-0 right-0 w-7 h-7 bg-white rounded-full flex items-center justify-center shadow hover:bg-gray-100" title="Change photo">
                  {uploadingPhoto ? <span className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600" /> : <Camera className="w-4 h-4 text-blue-600" />}
                </button>
                <input ref={photoRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold">{student.name || student.fullName}</h3>
                <p className="text-blue-100 text-sm">{student.email}</p>
                {student.registrationNumber && (
                  <span className="inline-block mt-1 px-2 py-0.5 bg-white/20 rounded text-xs font-mono">{student.registrationNumber}</span>
                )}
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 overflow-x-auto border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl px-4 pt-2">
            {TABS.map(t => (
              <button key={t.id} onClick={() => setActiveTab(t.id)}
                className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === t.id ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-800 dark:hover:text-gray-200'}`}>
                <t.icon className="w-4 h-4" />{t.label}
              </button>
            ))}
          </div>

          {/* ── PROFILE ── */}
          {activeTab === 'profile' && (
            <div className="space-y-4">
              {/* Edit / Save / Cancel actions — only on Profile tab */}
              <div className="flex justify-end gap-2">
                {editing ? (
                  <>
                    <button onClick={handleSave} disabled={saving}
                      className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium">
                      <Save className="w-4 h-4" />{saving ? 'Saving…' : 'Save'}
                    </button>
                    <button onClick={() => { setEditing(false); setFormData({ ...student }); }}
                      className="flex items-center gap-1.5 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-gray-700">
                      <X className="w-4 h-4" /> Cancel
                    </button>
                  </>
                ) : (
                  <button onClick={() => setEditing(true)}
                    className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium">
                    <Edit2 className="w-4 h-4" /> Edit Profile
                  </button>
                )}
              </div>

              <Section title="Basic Information" icon={User}>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  <Field label="Registration Number"  name="registrationNumber" value={editing ? formData.registrationNumber : student.registrationNumber} editing={editing} onChange={handleChange} />
                  <Field label="Full Name"             name="name"               value={editing ? formData.name : student.name}               editing={editing} onChange={handleChange} />
                  <Field label="Email"                 name="email"              value={editing ? formData.email : student.email}             editing={editing} onChange={handleChange} type="email" />
                  <Field label="Date of Birth"         name="dateOfBirth"        value={editing ? formData.dateOfBirth : student.dateOfBirth} editing={editing} onChange={handleChange} type="date" />
                  <Field label="Date of Admission"     name="dateOfAdmission"    value={editing ? formData.dateOfAdmission : student.dateOfAdmission} editing={editing} onChange={handleChange} type="date" />
                  <Field label="Admission Number"      name="admissionNumber"    value={editing ? formData.admissionNumber : student.admissionNumber} editing={editing} onChange={handleChange} />
                  <Field label="Gender"                name="gender"             value={editing ? formData.gender : student.gender}           editing={editing} onChange={handleChange} options={['Male','Female','Other','Prefer not to say']} />
                  <Field label="Blood Group"           name="bloodGroup"         value={editing ? formData.bloodGroup : student.bloodGroup}   editing={editing} onChange={handleChange} options={['A+','A-','B+','B-','AB+','AB-','O+','O-']} />
                  <Field label="Phone"                 name="phone"              value={editing ? formData.phone : student.phone}             editing={editing} onChange={handleChange} />
                </div>
              </Section>
              <Section title="Identity Documents" icon={Shield}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <Field label="Aadhar Number" name="aadharNumber" value={editing ? formData.aadharNumber : student.aadharNumber} editing={editing} onChange={handleChange} />
                  <Field label="PAN Number"    name="panNumber"    value={editing ? formData.panNumber    : student.panNumber}    editing={editing} onChange={handleChange} />
                </div>
              </Section>
              <Section title="Address" icon={MapPin}>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  <div className="sm:col-span-2 lg:col-span-3">
                    <Field label="Address" name="address" value={editing ? formData.address : student.address} editing={editing} onChange={handleChange} />
                  </div>
                  <Field label="City"    name="city"    value={editing ? formData.city    : student.city}    editing={editing} onChange={handleChange} />
                  <Field label="State"   name="state"   value={editing ? formData.state   : student.state}   editing={editing} onChange={handleChange} />
                  <Field label="Pincode" name="pincode" value={editing ? formData.pincode : student.pincode} editing={editing} onChange={handleChange} />
                </div>
              </Section>
              <Section title="Family Information" icon={Briefcase}>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  <Field label="Father's Name"        name="fatherName"        value={editing ? formData.fatherName        : student.fatherName}        editing={editing} onChange={handleChange} />
                  <Field label="Father's Occupation"  name="fathersOccupation" value={editing ? formData.fathersOccupation : student.fathersOccupation} editing={editing} onChange={handleChange} />
                  <Field label="Father's Contact"     name="fatherContact"     value={editing ? formData.fatherContact     : student.fatherContact}     editing={editing} onChange={handleChange} />
                  <Field label="Mother's Name"        name="motherName"        value={editing ? formData.motherName        : student.motherName}        editing={editing} onChange={handleChange} />
                  <Field label="Mother's Occupation"  name="mothersOccupation" value={editing ? formData.mothersOccupation : student.mothersOccupation} editing={editing} onChange={handleChange} />
                  <Field label="Mother's Contact"     name="motherContact"     value={editing ? formData.motherContact     : student.motherContact}     editing={editing} onChange={handleChange} />
                </div>
              </Section>
            </div>
          )}

          {/* ── FEES ── */}
          {activeTab === 'fees' && (
            <Section title="Fee Records" icon={CreditCard}>
              {loadingData
                ? <div className="flex justify-center py-8"><span className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" /></div>
                : fees.length === 0
                  ? <p className="text-gray-500 dark:text-gray-400 text-center py-8">No fee records found.</p>
                  : <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-200 dark:border-gray-600 text-left">
                            {['Fee Type','Batch','Amount','Paid','Outstanding','Status','Due Date'].map(h => (
                              <th key={h} className="py-2 px-3 text-xs font-semibold text-gray-500 uppercase">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                          {fees.map(f => (
                            <tr key={f.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                              <td className="py-2 px-3 capitalize">{f.feeType || '—'}</td>
                              <td className="py-2 px-3">{feesBatchNames[f.batchId] || f.batchName || '—'}</td>
                              <td className="py-2 px-3 font-medium">₹{f.amount ?? '—'}</td>
                              <td className="py-2 px-3 text-green-600">₹{f.paidAmount ?? 0}</td>
                              <td className="py-2 px-3 text-red-500">₹{f.outstandingAmount ?? '—'}</td>
                              <td className="py-2 px-3">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                  f.status === 'paid' ? 'bg-green-100 text-green-700' :
                                  f.status === 'partial' ? 'bg-blue-100 text-blue-700' :
                                  'bg-yellow-100 text-yellow-700'}`}>{f.status || '—'}</span>
                              </td>
                              <td className="py-2 px-3">{fmtDate(f.dueDate)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
              }
            </Section>
          )}

          {/* ── ATTENDANCE ── */}
          {activeTab === 'attendance' && (
            <Section title="Attendance" icon={ClipboardList}>
              {loadingData
                ? <div className="flex justify-center py-8"><span className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" /></div>
                : Object.values(attByBatch).length === 0
                  ? <p className="text-gray-500 dark:text-gray-400 text-center py-8">No attendance records found.</p>
                  : <div className="space-y-4">
                      {/* Summary cards */}
                      {Object.values(attByBatch).map(b => {
                        const pct = b.total > 0 ? Math.round((b.present / b.total) * 100) : 0;
                        return (
                          <div key={b.batchId} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-2">
                              <div>
                                <p className="font-medium text-gray-800 dark:text-white">{b.batchName}</p>
                                <p className="text-xs text-gray-500">{b.present} present / {b.total} classes</p>
                              </div>
                              <span className={`text-xl font-bold ${pct >= 75 ? 'text-green-600' : pct >= 50 ? 'text-yellow-600' : 'text-red-600'}`}>{pct}%</span>
                            </div>
                            <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-2">
                              <div className={`h-2 rounded-full ${pct >= 75 ? 'bg-green-500' : pct >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`} style={{ width: `${pct}%` }} />
                            </div>
                          </div>
                        );
                      })}
                      {/* Detail table */}
                      <div className="overflow-x-auto mt-4">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-gray-200 dark:border-gray-600 text-left">
                              {['Date','Batch','Status'].map(h => (
                                <th key={h} className="py-2 px-3 text-xs font-semibold text-gray-500 uppercase">{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                            {attendance.slice(0, 50).map(r => (
                              <tr key={r.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                                <td className="py-2 px-3">{r.date || fmtDate(r.markedAt)}</td>
                                <td className="py-2 px-3">{batchNames[r.batchId] || r.batchName || r.batchId || '—'}</td>
                                <td className="py-2 px-3">
                                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                    r.status === 'present' ? 'bg-green-100 text-green-700' :
                                    r.status === 'late'    ? 'bg-yellow-100 text-yellow-700' :
                                    'bg-red-100 text-red-700'}`}>{r.status || '—'}</span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
              }
            </Section>
          )}

          {/* ── RESULTS ── */}
          {activeTab === 'results' && (
            <Section title="Test & Exam Results" icon={Award}>
              {loadingData
                ? <div className="flex justify-center py-8"><span className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" /></div>
                : testResults.length === 0
                  ? <p className="text-gray-500 dark:text-gray-400 text-center py-8">No results found.</p>
                  : <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-200 dark:border-gray-600 text-left">
                            {['Test/Exam','Batch','Score','Grade','Date'].map(h => (
                              <th key={h} className="py-2 px-3 text-xs font-semibold text-gray-500 uppercase">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                          {testResults.map(r => (
                            <tr key={r.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                              <td className="py-2 px-3 font-medium">{r.testName || r.examName || '—'}</td>
                              <td className="py-2 px-3">{r.batchName || '—'}</td>
                              <td className="py-2 px-3">{r.score ?? r.marks ?? '—'}{r.totalMarks ? `/${r.totalMarks}` : ''}</td>
                              <td className="py-2 px-3"><span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">{r.grade || '—'}</span></td>
                              <td className="py-2 px-3">{fmtDate(r.date)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
              }
            </Section>
          )}

          {/* ── CERTIFICATES ── */}
          {activeTab === 'certificates' && (
            <div className="space-y-6">
              {/* All certificate cards in grid */}
              <Section title="Certificates" icon={FileText}>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* Transfer Certificate — special: opens live form */}
                  <div className="border border-blue-200 dark:border-blue-700 rounded-xl p-5 flex flex-col gap-3 hover:shadow-md transition-shadow bg-blue-50 dark:bg-blue-900/20 sm:col-span-2 lg:col-span-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center">
                          <FileText className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-800 dark:text-white text-sm">Transfer Certificate</p>
                          <p className="text-xs text-gray-500">Fill fields live · Preview · Download PDF</p>
                        </div>
                      </div>
                      <button
                        onClick={() => setShowTC(v => !v)}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors">
                        {showTC ? <><X className="w-4 h-4" /> Close</> : <><Edit2 className="w-4 h-4" /> Generate TC</>}
                      </button>
                    </div>

                    {/* Inline expandable TC form */}
                    {showTC && (
                      <div className="mt-4 border-t border-blue-200 dark:border-blue-700 pt-4">
                        <TCForm student={student} institute={institute} />
                      </div>
                    )}
                  </div>

                  {/* Other certificates */}
                  {CERTIFICATE_TYPES.filter(c => c.id !== 'transfer').map(cert => (
                    <div key={cert.id} className="border border-gray-200 dark:border-gray-700 rounded-xl p-5 flex flex-col items-center gap-3 hover:shadow-md transition-shadow bg-white dark:bg-gray-800 text-center">
                      <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                        <FileText className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-800 dark:text-white text-sm">{cert.label}</p>
                        <p className="text-xs text-gray-400">PDF · Instant download</p>
                      </div>
                      <button
                        onClick={() => buildCertPDF(cert, student, institute, setLoadingCert)}
                        disabled={loadingCert === cert.id}
                        className="flex items-center justify-center gap-2 w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium disabled:opacity-60">
                        {loadingCert === cert.id ? (
                          <><svg className="animate-spin w-4 h-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg> Downloading...</>
                        ) : (
                          <><Download className="w-4 h-4" /> Download</>
                        )}
                      </button>
                    </div>
                  ))}
                </div>
              </Section>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
